import * as React from 'react';
import { Link } from 'react-router-dom';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import {
  PageTitle, Card, Kpi, Section, Btn, Pill, StatusPill,
  Table, Th, Td, fmt, FairnessPill,
} from '../components/ui-kit';
import { ArrowUpRight, ShieldCheck } from 'lucide-react';

interface Mission { id: string; status: string; title?: string; }
interface Invoice { id: string; amount_ttc: number; }
interface Dash {
  company?: { name?: string; domain?: string; sector?: string } | null;
  contract?: { status?: string; offer_type?: string; amount_ttc?: number } | null;
  missions?: { total: number; active: number; items: Mission[] };
  invoices?: Invoice[];
  experts_proposed?: number;
  opportunities?: number;
}

export default function Dashboard() {
  const { getToken, principal } = useXyqarAuth();
  const [d, setD] = React.useState<Dash | null>(null);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const tok = await getToken().catch(() => '');
        const j = await apiGet<Dash>('/api/v1/client/dashboard', tok);
        setD(j);
      } catch (e: any) {
        setErr(e.message);
        setD({});
      }
    })();
  }, [getToken]);

  const totalInvoices = (d?.invoices ?? []).reduce((s, i) => s + (Number(i.amount_ttc) || 0), 0);
  const name = d?.company?.name ?? principal?.email?.split('@')[0] ?? 'Client';
  const latestInvoices = (d?.invoices ?? []).slice(0, 5);
  const activeMissions = (d?.missions?.items ?? []).filter((mission) => mission.status !== 'closed').length;

  return (
    <div>
      <PageTitle
        kicker="Client control room"
        title={name}
        right={
          <div className="flex items-center gap-2">
            <StatusPill ok label="Blind matching live" />
            <Pill tone="live"><ShieldCheck className="h-3 w-3" /> audit chain active</Pill>
          </div>
        }
      />

      {err && (
        <Card className="mb-6 border-amber-500/30 bg-amber-500/5">
          <p className="text-amber-300 text-xs font-mono">Dashboard data partial · {err}</p>
          <p className="text-white/40 text-[11px] mt-1">Use the Missions tab to access your live engagements.</p>
        </Card>
      )}

      <Section title="At a glance" hint="From brief to reveal, this is your live hiring signal">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi accent="blue"   label="Active missions"   value={fmt(d?.missions?.active ?? 0)}     hint={`${fmt(d?.missions?.total ?? 0)} total`} />
          <Kpi accent="green"  label="Experts proposed"  value={fmt(d?.experts_proposed ?? 0)}     hint="anonymised, ranked" />
          <Kpi accent="amber"  label="Open invoices €"   value={fmt(Math.round(totalInvoices))}    hint={`${(d?.invoices ?? []).length} pending`} />
          <Kpi               label="Opportunities"     value={fmt(d?.opportunities ?? 0)}        hint="in pipeline" />
        </div>
      </Section>

      <Section title="Action lanes" hint="Move from monitoring to execution without hunting for the right page">
        <div className="flex flex-wrap gap-3">
          <Link to="/missions"><Btn primary>Pilot missions <ArrowUpRight className="h-3 w-3" /></Btn></Link>
          <Link to="/marketplace"><Btn>Open blind marketplace</Btn></Link>
          <Link to="/account"><Btn>Review account controls</Btn></Link>
        </div>
      </Section>

      <Section title="Account posture" hint="What is structurally ready on the account right now">
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Company</p>
            <p className="mt-2 text-white text-sm">{d?.company?.name ?? name}</p>
            <p className="mt-1 text-xs text-white/50">{d?.company?.sector ?? 'Sector not declared yet'}</p>
            <p className="mt-3 text-[11px] text-white/35">{d?.company?.domain ?? 'Domain not available in dashboard payload'}</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Commercial lane</p>
            <p className="mt-2 text-white text-sm">{d?.contract?.offer_type ?? 'No offer configured yet'}</p>
            <div className="mt-3 flex items-center gap-2">
              <Pill>{d?.contract?.status ?? 'no contract'}</Pill>
            </div>
            <p className="mt-3 text-[11px] text-white/35">Visible contract value: {fmt(Math.round(d?.contract?.amount_ttc ?? 0))} € TTC</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Execution coverage</p>
            <p className="mt-2 text-white text-sm">{fmt(activeMissions)} live missions under review</p>
            <p className="mt-1 text-xs text-white/50">{fmt(d?.experts_proposed ?? 0)} experts proposed in current payload</p>
            <p className="mt-3 text-[11px] text-white/35">{fmt((d?.invoices ?? []).length)} invoices visible on dashboard for {fmt(Math.round(totalInvoices))} € TTC</p>
          </Card>
        </div>
      </Section>

      <Section
        title="Recent missions"
        hint="Review active mandates, shortlist progress, and next contractual step"
        right={<Link to="/missions"><Btn>Open all <ArrowUpRight className="h-3 w-3" /></Btn></Link>}
      >
        {(d?.missions?.items ?? []).length === 0 && (
          <Card><p className="text-white/40 text-sm">No mission yet. Start with a brief to receive a ranked blind shortlist, or open the <Link to="/marketplace" className="underline">blind marketplace</Link> for sensitive mandates.</p></Card>
        )}
        {(d?.missions?.items ?? []).length > 0 && (
          <Table>
            <thead>
              <tr><Th>Mission</Th><Th>Status</Th><Th>Fairness</Th><Th></Th></tr>
            </thead>
            <tbody>
              {(d?.missions?.items ?? []).map((m) => (
                <tr key={m.id} className="border-t border-white/5 hover:bg-white/5">
                  <Td><span className="text-white">{m.title ?? m.id}</span><div className="text-[10px] text-white/30 font-mono">{m.id}</div></Td>
                  <Td><Pill>{m.status}</Pill></Td>
                  <Td><FairnessPill grade="GREEN" /></Td>
                  <Td className="text-right"><Link to={`/missions/${m.id}`}><Btn>Open</Btn></Link></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Section title="Invoice watch" hint="Keep a simple view on financial exposure from the dashboard">
        {latestInvoices.length === 0 && (
          <Card><p className="text-white/40 text-sm">No invoices exposed in the current dashboard payload.</p></Card>
        )}
        {latestInvoices.length > 0 && (
          <Table>
            <thead>
              <tr><Th>Invoice</Th><Th>Amount TTC</Th><Th>Coverage</Th></tr>
            </thead>
            <tbody>
              {latestInvoices.map((invoice) => (
                <tr key={invoice.id} className="border-t border-white/5 hover:bg-white/5">
                  <Td><span className="text-white font-mono text-xs">{invoice.id}</span></Td>
                  <Td>{fmt(Math.round(invoice.amount_ttc ?? 0))} €</Td>
                  <Td><Pill>{d?.missions?.active ? 'backed by active missions' : 'awaiting mission activity'}</Pill></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Section title="Why Xyqar is safer than a classic marketplace" hint="What stays controlled in every mandate">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Anonymity</p>
            <p className="text-white text-sm mt-2">Every candidate enters as a <span className="font-mono">wolf_id</span>. Identity is revealed only after a qualified mutual step.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Audit chain</p>
            <p className="text-white text-sm mt-2">Each match is sealed in a tamper-evident chain so audit and compliance teams can replay decisions.</p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">No LLM in scoring</p>
            <p className="text-white text-sm mt-2">Wolf Brain v3 keeps scoring deterministic and persists the underlying dimensions for every match.</p>
          </Card>
        </div>
      </Section>
    </div>
  );
}
